import { Button, ButtonSize, ButtonVariant, Modal, ModalSize, Spinner, SpinnerSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCharacterTranslation, useTranslatedLanguage, useUserSettings } from "@next-platform/client-common-core";

import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

import { TranslationStatusModalProps } from "./";

export const TranslationStatusModal = ({
  isOpen,
  onOpenChange,
  characterId,
  onTranslationSuccess,
  onTranslationError,
  dataTestId = "",
}: TranslationStatusModalProps) => {
  const { getTranslatedLanguage } = useTranslatedLanguage();
  const { userChatLanguageName } = useUserSettings();
  const languageLabel = getTranslatedLanguage(userChatLanguageName);
  const {
    translationProgress,
    translationStatus,
    errorMessage: translationError,
    isTranslating,
    onUserStartTranslation,
    resetStatus,
    resetError,
  } = useCharacterTranslation();

  const handleCloseModal = () => {
    onOpenChange(false);
    resetStatus();
    resetError();
  };

  const handleTranslation = () => {
    if (characterId) {
      onUserStartTranslation(characterId, {
        onSuccess: onTranslationSuccess,
        onError: onTranslationError,
      });
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={handleCloseModal} size={ModalSize.SMALL}>
      <div className="p-4" data-testid={dataTestId}>
        <Text variant={TextVariants.HEADING_4_REGULAR}>
          <Trans i18nKey="chatbot:modal.translation.title" />
        </Text>

        {!!translationError && <div className="mb-4 p-3 bg-red-100 text-red-800 rounded">{translationError}</div>}

        <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR}>
          <Trans i18nKey="chatbot:modal.translation.description" values={{ language: languageLabel }} />
        </Text>
        <Row className="mt-4 mb-4">
          <Text variant={TextVariants.PARAGRAPH_XSMALL_REGULAR}>
            <Trans i18nKey="chatbot:modal.translation.note" />
          </Text>
        </Row>

        {!!isTranslating && (
          <Col className="gap-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${translationProgress}%` }}></div>
            </div>
            <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-center mt-2">
              {translationStatus} ({translationProgress}%)
            </Text>
            <div className="flex justify-center mt-4">
              <Spinner size={SpinnerSize.MEDIUM} />
            </div>
          </Col>
        )}

        <Row className="justify-end mt-6">
          {!isTranslating && (
            <Button variant={ButtonVariant.SECONDARY} size={ButtonSize.MEDIUM} onClick={handleCloseModal}>
              <Trans i18nKey="common:cancel" />
            </Button>
          )}
          {!isTranslating && (
            <Button variant={ButtonVariant.CONTAINED} size={ButtonSize.MEDIUM} onClick={handleTranslation} className="ml-2">
              <Trans i18nKey="chatbot:modal.translation.cta.startTranslation" />
            </Button>
          )}
        </Row>
      </div>
    </Modal>
  );
};
